import { useCallback, useMemo, useState } from "react";
import Big from "big.js";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import useBookingAddOns, {
  BookingAddOn,
} from "../AppointmentBookingContext/useBookingAddOns";
import { BookingPackage } from "../AppointmentBookingContext/useBookingPackages";

export type SelectedAddOnService = {
  id: string;
  appointmentOfferingId: string;
  offeringsCount: number;
  isAdded: boolean;
};

export type SuggestedAddOn = {
  centerAddOn: BookingAddOn;
  isSelected: boolean;
  selectedPackages: SelectedAddOnService[];
  selectedOfferings: SelectedAddOnService[];
  quantity: number;
  price: number;
  appliedPrice: number;
};

type SelectedAddOnSelections = {
  isSelected: boolean;
  selectedPackageIds: string[];
  selectedOfferingIds: string[];
};

export type AddOnSelectionHandlerProps = {
  addOnId: string;
  selectedPackageIds: string[];
  selectedOfferingIds: string[];
};

// Returns true if the add-on applies to study-level items
const isStudyAddOn = (addOn: BookingAddOn): boolean =>
  addOn.appliesTo === "study";

// Determines whether an add-on should be suggested given the appointment context
const computeIsSuggestedForAppointment = (
  addOn: BookingAddOn,
  appointmentData: ConfirmedAppointment,
  isStatAvailableForAppointmentTime: boolean,
): boolean => {
  if (addOn.type === "stat" && !isStatAvailableForAppointmentTime) return false;
  if (isStudyAddOn(addOn)) {
    const allOfferingsHaveAddOn = appointmentData.offerings.every((offering) =>
      offering.addOnIds.includes(addOn.id),
    );

    const allPackagesHaveAddOn = appointmentData.packages.every((pkg) =>
      pkg.addOnIds.includes(addOn.id),
    );

    return !(allOfferingsHaveAddOn && allPackagesHaveAddOn);
  }

  const alreadyInAppointment = appointmentData.addOns.some(
    (appointmentAddOn) => appointmentAddOn.centerAddOnId === addOn.id,
  );

  return !alreadyInAppointment;
};

// Builds the selected services payload for a study-level add-on
const getSelectedServicesForStudyAddOn = (
  addOn: BookingAddOn,
  appointmentData: ConfirmedAppointment,
  centerPackages: BookingPackage[],
  addOnSelectionsById: Record<string, SelectedAddOnSelections>,
) => {
  if (!isStudyAddOn(addOn)) {
    return { selectedPackages: [], selectedOfferings: [] };
  }

  const selectedPackages: SelectedAddOnService[] = centerPackages.map(
    (pkg) => ({
      id: pkg.id,
      offeringsCount: pkg.globalStudyIds.length,
      appointmentOfferingId: "",
      isAdded:
        addOnSelectionsById[addOn.id]?.selectedPackageIds?.includes(pkg.id) ??
        false,
    }),
  );

  const selectedOfferings: SelectedAddOnService[] =
    appointmentData.offerings.map((offering) => ({
      id: offering.centerOfferingId,
      appointmentOfferingId: offering.id,
      offeringsCount: 1,
      isAdded:
        addOnSelectionsById[addOn.id]?.selectedOfferingIds?.includes(
          offering.id,
        ) ?? false,
    }));

  return { selectedPackages, selectedOfferings };
};

const useSuggestedAddOns = ({
  appointmentData,
  centerPackages,
  studiesCount,
  isStatAvailableForAppointmentTime,
  isCenterUser,
}: {
  appointmentData: ConfirmedAppointment;
  centerPackages?: BookingPackage[] | null;
  studiesCount: number;
  isStatAvailableForAppointmentTime: boolean;
  isCenterUser?: boolean;
}): {
  suggestedAddOns: SuggestedAddOn[];
  onSelect: (props: AddOnSelectionHandlerProps) => void;
  onDeselect: (addOnId: string) => void;
  onQuantityChange: (addOnId: string, quantity: number) => void;
  onReset: () => void;
} | null => {
  const [addOnSelectionsById, setAddOnSelectionsById] = useState<
    Record<string, SelectedAddOnSelections>
  >({});
  const [quantitiesById, setQuantitiesById] = useState<Record<string, number>>(
    {},
  );

  const { data: addOns } = useBookingAddOns(
    appointmentData.center.id,
    isCenterUser,
  );

  const onSelect = useCallback(
    ({
      addOnId,
      selectedPackageIds,
      selectedOfferingIds,
    }: AddOnSelectionHandlerProps) => {
      setAddOnSelectionsById((previousSelections) => ({
        ...previousSelections,
        [addOnId]: {
          isSelected: true,
          selectedPackageIds,
          selectedOfferingIds,
        },
      }));
    },
    [],
  );

  const onDeselect = useCallback((addOnId: string) => {
    setAddOnSelectionsById((previousSelections) => ({
      ...previousSelections,
      [addOnId]: {
        isSelected: false,
        selectedPackageIds: [],
        selectedOfferingIds: [],
      },
    }));
    setQuantitiesById((previousQuantities) => ({
      ...previousQuantities,
      [addOnId]: 1,
    }));
  }, []);

  const onQuantityChange = useCallback((addOnId: string, quantity: number) => {
    setQuantitiesById((previousQuantities) => ({
      ...previousQuantities,
      [addOnId]: quantity,
    }));
  }, []);

  const onReset = () => {
    setAddOnSelectionsById({});
    setQuantitiesById({});
  };

  const suggestedCenterAddOns = useMemo(
    () =>
      (addOns ?? []).filter((addOn) =>
        computeIsSuggestedForAppointment(
          addOn,
          appointmentData,
          isStatAvailableForAppointmentTime,
        ),
      ),
    [addOns, appointmentData, isStatAvailableForAppointmentTime],
  );

  const suggestedAddOns = useMemo<SuggestedAddOn[]>(
    () =>
      suggestedCenterAddOns.map((addOn) => {
        const { selectedPackages, selectedOfferings } =
          getSelectedServicesForStudyAddOn(
            addOn,
            appointmentData,
            centerPackages || [],
            addOnSelectionsById,
          );

        const appliedSingleOfferings = appointmentData.offerings.filter(
          (offering) =>
            selectedOfferings.some(
              (selectedOffering) =>
                selectedOffering.appointmentOfferingId === offering.id &&
                selectedOffering.isAdded,
            ),
        );

        const isSelected = addOnSelectionsById[addOn.id]?.isSelected ?? false;
        const quantity = quantitiesById[addOn.id] ?? 1;

        let priceBig = Big(addOn.price);

        if (addOn.appliesTo === "study") {
          const addedPackageOfferingsCount = (centerPackages || [])
            .filter((pkg) =>
              selectedPackages.some(
                (selectedPackage) =>
                  selectedPackage.id === pkg.id && selectedPackage.isAdded,
              ),
            )
            .map((pkg) => pkg.offerings)
            .flat().length;

          const totalCount =
            appliedSingleOfferings.length + addedPackageOfferingsCount;

          priceBig = Big(addOn.price).times(totalCount);
        }

        if (addOn.appliesTo === "appointment" && addOn.isPerStudyPricing) {
          priceBig = Big(addOn.price).times(studiesCount);
        }

        priceBig = priceBig.times(quantity);
        const price = priceBig.toNumber();

        return {
          centerAddOn: addOn,
          isSelected,
          selectedPackages,
          selectedOfferings,
          quantity,
          price,
          appliedPrice: isSelected ? price : 0,
        };
      }),
    [
      suggestedCenterAddOns,
      appointmentData,
      centerPackages,
      addOnSelectionsById,
      quantitiesById,
      studiesCount,
    ],
  );

  return addOns
    ? { suggestedAddOns, onSelect, onDeselect, onQuantityChange, onReset }
    : null;
};

export default useSuggestedAddOns;
